import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";

interface PlatformCategory {
  id: number;
  categoryCode: string;
  categoryName: string;
  categoryDescription: string;
  parentCategoryId?: number;
  isHidden: boolean;
  subCategoryVOs: PlatformCategory[];
}

interface PlatformCategoryContextType {
  platformCategories: PlatformCategory | null;
  loading: boolean;
  error: string | null;
  findCategoryNameById: (
    categoryId: number,
    categories?: PlatformCategory
  ) => string | null;
  getCategoryNames: (categoryIds: number[]) => string[];
}

const PlatformCategoryContext = createContext<
  PlatformCategoryContextType | undefined
>(undefined);

export const usePlatformCategories = () => {
  const context = useContext(PlatformCategoryContext);
  if (context === undefined) {
    throw new Error(
      "usePlatformCategories must be used within a PlatformCategoryProvider"
    );
  }
  return context;
};

interface PlatformCategoryProviderProps {
  children: ReactNode;
}

export const PlatformCategoryProvider: React.FC<
  PlatformCategoryProviderProps
> = ({ children }) => {
  const [platformCategories, setPlatformCategories] =
    useState<PlatformCategory | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPlatformCategories() {
      try {
        setLoading(true);
        setError(null);
        const res = await fetch(
          `${
            import.meta.env.VITE_REACT_APP_CORE_ENGINE_URL
          }/platformCategory/getPlatformCategoryTree?platformCategoryCode=ROOT`,
          { credentials: "include" }
        );
        const data = await res.json();
        setPlatformCategories(data);
      } catch (err) {
        console.error("Failed to fetch platform categories:", err);
        setError("Failed to fetch platform categories");
      } finally {
        setLoading(false);
      }
    }
    fetchPlatformCategories();
  }, []);

  // Helper function to find category name by ID recursively
  const findCategoryNameById = (
    categoryId: number,
    categories?: PlatformCategory
  ): string | null => {
    const targetCategories = categories || platformCategories;
    if (!targetCategories || !categoryId) return null;

    // Search in subCategoryVOs recursively
    const searchInCategories = (cats: PlatformCategory[]): string | null => {
      for (const cat of cats) {
        if (cat.id === categoryId) {
          return cat.categoryName;
        }
        if (cat.subCategoryVOs && cat.subCategoryVOs.length > 0) {
          const found = searchInCategories(cat.subCategoryVOs);
          if (found) return found;
        }
      }
      return null;
    };

    return searchInCategories(targetCategories.subCategoryVOs || []);
  };

  // Helper function to get category names from IDs
  const getCategoryNames = (categoryIds: number[]): string[] => {
    if (!categoryIds || !platformCategories) return [];

    return categoryIds
      .map((id) => findCategoryNameById(id))
      .filter((name): name is string => name !== null);
  };

  const value: PlatformCategoryContextType = {
    platformCategories,
    loading,
    error,
    findCategoryNameById,
    getCategoryNames,
  };

  return (
    <PlatformCategoryContext.Provider value={value}>
      {children}
    </PlatformCategoryContext.Provider>
  );
};
